<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import ReportsAPI from 'dashboard/api/reports';
import Spinner from 'shared/components/Spinner.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';

const PER_PAGE = 20;
const NUMBERS_PER_PAGE = 10;

const { t } = useI18n();
const route = useRoute();
const activeTab = ref('meta');
const loading = ref(false);
const messagesLoading = ref(false);
const exporting = ref(false);
const report = ref(null);
const filters = ref({ from: 0, to: 0 });
const page = ref(1);
const chargedMessages = ref([]);
const totalCharged = ref(0);
const numbersPage = ref(1);

const tabs = computed(() => [
  { id: 'meta', label: t('TARIFF_REPORTS.TABS.META') },
  { id: 'iungo', label: t('TARIFF_REPORTS.TABS.IUNGO') },
]);

const current = computed(() => report.value?.[activeTab.value] || null);
const pagedNumbers = computed(() =>
  (current.value?.numbers || []).slice(
    (numbersPage.value - 1) * NUMBERS_PER_PAGE,
    numbersPage.value * NUMBERS_PER_PAGE
  )
);
const hasChannel = computed(
  () => (report.value?.channels?.[activeTab.value] || 0) > 0
);

const brl = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value) || 0
  );

const usd = value =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(value) || 0
  );

const brlUnit = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(Number(value) || 0);

const usdUnit = value =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(Number(value) || 0);

const qty = value => new Intl.NumberFormat('pt-BR').format(Number(value) || 0);

const rateLine = computed(() => {
  const rates = report.value?.rates;
  if (!rates) return '';
  return t('TARIFF_REPORTS.RATES', {
    marketing: brl(rates.marketing_brl),
    utility: brl(rates.utility_brl),
    service: brl(rates.service_brl),
    free: qty(rates.free_service_per_number),
  });
});

const summaryRows = computed(() => {
  const totals = current.value?.totals;
  if (!totals) return [];

  const rows = [
    [
      t('TARIFF_REPORTS.SUMMARY.MARKETING'),
      qty(totals.marketing),
      brl(totals.marketing_brl),
    ],
    [
      t('TARIFF_REPORTS.SUMMARY.UTILITY'),
      qty(totals.utility),
      brl(totals.utility_brl),
    ],
    [
      t('TARIFF_REPORTS.SUMMARY.AUTHENTICATION'),
      qty(totals.authentication),
      brl(totals.authentication_brl),
    ],
    [
      t('TARIFF_REPORTS.SUMMARY.SERVICE'),
      qty(totals.service_billable),
      brl(totals.service_brl),
    ],
    [t('TARIFF_REPORTS.SUMMARY.INCOMING'), qty(totals.incoming), brl(0)],
  ];

  if (activeTab.value === 'iungo') {
    rows.push(
      [
        t('TARIFF_REPORTS.SUMMARY.GUPSHUPPLATFORM'),
        qty(totals.platform_messages),
        usd(totals.gupshup_platform_usd),
      ],
      [
        t('TARIFF_REPORTS.SUMMARY.GUPSHUPMARKUP'),
        qty(totals.marketing),
        brl(totals.gupshup_marketing_markup_brl),
      ]
    );
  }

  return rows;
});

const categoryLabel = category => {
  const labels = {
    marketing: t('TARIFF_REPORTS.SUMMARY.MARKETING'),
    utility: t('TARIFF_REPORTS.SUMMARY.UTILITY'),
    authentication: t('TARIFF_REPORTS.SUMMARY.AUTHENTICATION'),
    service: t('TARIFF_REPORTS.SUMMARY.SERVICE'),
  };
  return labels[category] || category;
};

const formatDate = value => {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const conversationLink = item =>
  frontendURL(
    `accounts/${route.params.accountId}/conversations/${item.display_id}`,
    { messageId: item.message_id }
  );

const loadSummary = async () => {
  loading.value = true;
  try {
    const { data } = await ReportsAPI.getWhatsappTariff(filters.value);
    report.value = data;
  } catch {
    useAlert(t('TARIFF_REPORTS.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

const loadMessages = async () => {
  messagesLoading.value = true;
  try {
    const { data } = await ReportsAPI.getWhatsappTariffMessages({
      ...filters.value,
      provider: activeTab.value,
      page: page.value,
      perPage: PER_PAGE,
    });
    chargedMessages.value = data.messages || [];
    totalCharged.value = data.pagination?.total_count || 0;
  } catch {
    useAlert(t('TARIFF_REPORTS.FETCH_FAILED'));
  } finally {
    messagesLoading.value = false;
  }
};

const onFilterChange = ({ from, to }) => {
  filters.value = { from, to };
  page.value = 1;
  numbersPage.value = 1;
  loadSummary();
  loadMessages();
};

const selectTab = tabId => {
  if (activeTab.value === tabId) return;
  activeTab.value = tabId;
  page.value = 1;
  numbersPage.value = 1;
  loadMessages();
};

const handlePageChange = nextPage => {
  page.value = nextPage;
  loadMessages();
};

const exportReport = async () => {
  exporting.value = true;
  try {
    const response = await ReportsAPI.downloadWhatsappTariff({
      ...filters.value,
      provider: activeTab.value,
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `tarifacao-${activeTab.value}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  } catch {
    useAlert(t('TARIFF_REPORTS.EXPORT_FAILED'));
  } finally {
    exporting.value = false;
  }
};

const phoneLabel = phoneNumber => phoneNumber || t('TARIFF_REPORTS.NO_PHONE');
</script>

<template>
  <ReportHeader
    :header-title="$t('TARIFF_REPORTS.HEADER')"
    :header-description="$t('TARIFF_REPORTS.DESCRIPTION')"
  >
    <V4Button
      :label="$t('TARIFF_REPORTS.EXPORT')"
      icon="i-lucide-download"
      size="sm"
      :disabled="exporting || !hasChannel"
      :is-loading="exporting"
      @click="exportReport"
    />
  </ReportHeader>

  <div class="flex flex-col gap-4">
    <ReportFilterSelector
      :show-agents-filter="false"
      :show-group-by-filter="false"
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <div class="flex gap-2 border-b border-n-weak" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="px-4 py-2 text-sm font-medium border-b-2 -mb-px"
        :class="
          activeTab === tab.id
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11'
        "
        :aria-selected="activeTab === tab.id"
        @click="selectTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <template v-else-if="report">
      <p v-if="!hasChannel" class="text-sm text-n-slate-11">
        {{ $t('TARIFF_REPORTS.NO_CHANNEL') }}
      </p>

      <template v-else>
        <p class="text-sm text-n-slate-11">{{ rateLine }}</p>
        <p
          v-if="current.totals.service_before_rule > 0"
          class="text-sm text-n-slate-11"
        >
          {{
            $t('TARIFF_REPORTS.SERVICE_BEFORE_RULE', {
              count: qty(current.totals.service_before_rule),
            })
          }}
        </p>

        <div
          class="rounded-xl outline outline-1 outline-n-container bg-n-solid-2 px-6 py-5"
        >
          <p class="text-sm text-n-slate-11">
            {{ $t('TARIFF_REPORTS.META_TOTAL') }}
          </p>
          <p class="mt-1 text-2xl font-medium text-n-slate-12">
            {{ brl(current.totals.meta_total_brl) }}
          </p>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead>
              <tr class="text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.SUMMARY.CATEGORY') }}
                </th>
                <th class="py-2 pr-4 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.SUMMARY.MESSAGES') }}
                </th>
                <th class="py-2 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.SUMMARY.AMOUNT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in summaryRows"
                :key="row[0]"
                class="border-b border-n-weak text-n-slate-12"
              >
                <td class="py-2 pr-4">{{ row[0] }}</td>
                <td class="py-2 pr-4 text-right">{{ row[1] }}</td>
                <td class="py-2 text-right">{{ row[2] }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('TARIFF_REPORTS.NUMBERS_TITLE') }}
        </h3>
        <p v-if="current.numbers.length === 0" class="text-sm text-n-slate-11">
          {{ $t('TARIFF_REPORTS.NO_MESSAGES') }}
        </p>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead>
              <tr class="text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.TABLE.PHONE') }}
                </th>
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.TABLE.INBOX') }}
                </th>
                <th class="py-2 pr-4 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.TABLE.MARKETING') }}
                </th>
                <th class="py-2 pr-4 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.TABLE.UTILITY') }}
                </th>
                <th class="py-2 pr-4 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.TABLE.AUTHENTICATION') }}
                </th>
                <th class="py-2 pr-4 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.TABLE.SERVICE') }}
                </th>
                <th class="py-2 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.TABLE.TOTAL') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in pagedNumbers"
                :key="item.phone_number || item.inboxes.join('-')"
                class="border-b border-n-weak text-n-slate-12"
              >
                <td class="py-2 pr-4">{{ phoneLabel(item.phone_number) }}</td>
                <td class="py-2 pr-4">{{ item.inboxes.join(', ') }}</td>
                <td class="py-2 pr-4 text-right">{{ qty(item.marketing) }}</td>
                <td class="py-2 pr-4 text-right">{{ qty(item.utility) }}</td>
                <td class="py-2 pr-4 text-right">
                  {{ qty(item.authentication) }}
                </td>
                <td class="py-2 pr-4 text-right">
                  {{ qty(item.service_billable) }}
                </td>
                <td class="py-2 text-right">{{ brl(item.meta_total_brl) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaginationFooter
          v-if="current.numbers.length > NUMBERS_PER_PAGE"
          class="before:hidden max-w-full"
          :current-page="numbersPage"
          :total-items="current.numbers.length"
          :items-per-page="NUMBERS_PER_PAGE"
          @update:current-page="numbersPage = $event"
        />

        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('TARIFF_REPORTS.CHARGED_TITLE') }}
        </h3>
        <div
          v-if="messagesLoading"
          class="flex items-center justify-center py-8"
        >
          <Spinner />
        </div>
        <p
          v-else-if="chargedMessages.length === 0"
          class="text-sm text-n-slate-11"
        >
          {{ $t('TARIFF_REPORTS.NO_CHARGED') }}
        </p>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead>
              <tr class="text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.DATE') }}
                </th>
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.CATEGORY') }}
                </th>
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.CONVERSATION') }}
                </th>
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.CONTACT') }}
                </th>
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.INBOX') }}
                </th>
                <th class="py-2 pr-4 font-medium">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.CONTENT') }}
                </th>
                <th class="py-2 pr-4 font-medium text-right">
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.AMOUNT') }}
                </th>
                <th
                  v-if="activeTab === 'iungo'"
                  class="py-2 pr-4 font-medium text-right"
                >
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.PLATFORM') }}
                </th>
                <th
                  v-if="activeTab === 'iungo'"
                  class="py-2 pr-4 font-medium text-right"
                >
                  {{ $t('TARIFF_REPORTS.MESSAGES_TABLE.MARKUP') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in chargedMessages"
                :key="item.message_id"
                class="border-b border-n-weak text-n-slate-12"
              >
                <td class="py-2 pr-4 whitespace-nowrap">
                  {{ formatDate(item.created_at) }}
                </td>
                <td class="py-2 pr-4">{{ categoryLabel(item.category) }}</td>
                <td class="py-2 pr-4 whitespace-nowrap">
                  <router-link
                    class="text-n-brand underline"
                    :to="conversationLink(item)"
                  >
                    {{ item.display_id }}
                  </router-link>
                </td>
                <td class="py-2 pr-4">{{ item.contact_name }}</td>
                <td class="py-2 pr-4">{{ item.inbox_name }}</td>
                <td class="py-2 pr-4 max-w-xs truncate">
                  {{ item.content || $t('TARIFF_REPORTS.NO_CONTENT') }}
                </td>
                <td class="py-2 pr-4 text-right whitespace-nowrap">
                  {{ brlUnit(item.amount_brl) }}
                </td>
                <td
                  v-if="activeTab === 'iungo'"
                  class="py-2 pr-4 text-right whitespace-nowrap"
                >
                  {{ usdUnit(item.gupshup_platform_usd) }}
                </td>
                <td
                  v-if="activeTab === 'iungo'"
                  class="py-2 pr-4 text-right whitespace-nowrap"
                >
                  {{ brlUnit(item.gupshup_marketing_markup_brl) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <PaginationFooter
          v-if="totalCharged > PER_PAGE"
          class="before:hidden max-w-full"
          :current-page="page"
          :total-items="totalCharged"
          :items-per-page="PER_PAGE"
          @update:current-page="handlePageChange"
        />
      </template>
    </template>
  </div>
</template>
